import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Play, RefreshCw, Pause, ExternalLink } from 'lucide-react';

const SEV = { critical: 'bg-rose-50 text-rose-700', high: 'bg-orange-50 text-orange-700', medium: 'bg-amber-50 text-amber-700', low: 'bg-slate-100 text-slate-600' };

export default function CourseQaAllPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [auto, setAuto] = useState(false);
  const autoRef = useRef(false);

  const load = () => {
    setLoading(true);
    api.get('/course-qa/courses')
      .then(r => setCourses(r.data.courses))
      .catch(e => toast.error(e.response?.data?.detail || 'Could not load the catalogue'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const auditOne = async (slug) => {
    setBusy(slug);
    try {
      const r = await api.post('/course-qa/audit', { slug });
      setCourses(p => p.map(c => c.slug === slug ? { ...c, audit: { id: r.data.id, counts: r.data.counts, audited_at: r.data.audited_at, audited_by: r.data.audited_by, complete: r.data.complete } } : c));
      return true;
    } catch (e) {
      toast.error(`${slug}: ${e.response?.data?.detail || 'audit failed'}`);
      return false;
    } finally { setBusy(''); }
  };

  const runNext = async () => {
    autoRef.current = true;
    setAuto(true);
    for (const c of courses) {
      if (!autoRef.current) break;
      if (c.audit) continue;
      await auditOne(c.slug);
    }
    autoRef.current = false;
    setAuto(false);
    toast.success('Finished the pending pages');
  };

  const stop = () => { autoRef.current = false; setAuto(false); };
  const pending = courses.filter(c => !c.audit).length;

  const navTab = (to, label) => (
    <button key={to} onClick={() => navigate(to)}
      className={`text-xs font-medium px-2.5 py-1 rounded-sm transition-colors ${location.pathname === to ? 'text-white' : 'text-slate-300 hover:text-white'}`}
      data-testid={`qa-nav-${label.toLowerCase().replace(/ /g, '-')}`}>{label}</button>
  );

  return (
    <div className="-m-4 sm:-m-6" data-testid="course-qa-all-page">
      <div className="bg-[#0f2b4c] px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-baseline gap-3">
          <span className="text-white text-base sm:text-lg font-bold tracking-tight">Course Page QA</span>
          <span className="text-slate-300 text-xs hidden sm:inline">Every course, page by page</span>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {navTab('/course-qa', 'Audit a page')}
          {navTab('/course-qa/all', 'Audit all courses')}
          {navTab('/course-qa/defects', 'Defect board')}
          {navTab('/course-qa/rules', 'Rules guide')}
          {user?.role !== 'course_qa' && navTab('/course-qa/management', 'Management')}
        </div>
      </div>
      <div className="p-4 sm:p-6 bg-slate-50 min-h-[calc(100vh-7rem)] space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-lg font-bold text-slate-800">All Courses</h1>
          <p className="text-xs text-slate-400 mt-1">{courses.length} courses in the catalogue · {pending} not audited yet · counts show issues still open</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} className="rounded-sm" data-testid="qa-all-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          {auto ? (
            <Button size="sm" variant="outline" onClick={stop} className="rounded-sm" data-testid="qa-all-stop">
              <Pause className="w-3.5 h-3.5 mr-1.5" /> Stop
            </Button>
          ) : (
            <Button size="sm" onClick={runNext} disabled={!pending} className="rounded-sm" data-testid="qa-all-run">
              <Play className="w-3.5 h-3.5 mr-1.5" /> Audit pending pages
            </Button>
          )}
        </div>
      </div>

      <div className="border border-slate-200 rounded-sm bg-white overflow-hidden">
        {loading && <div className="p-6 text-center text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading catalogue…</div>}
        {courses.map(c => (
          <div key={c.slug} className="border-b border-slate-100 last:border-0 px-3 py-2.5 flex items-center justify-between gap-3 hover:bg-slate-50" data-testid={`qa-course-${c.slug}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-slate-800 truncate">{c.title}</span>
                {c.page_status && <Badge variant="outline" className="text-[10px] rounded-sm capitalize">{c.page_status}</Badge>}
                {c.audit && !c.audit.complete && <Badge variant="outline" className="text-[10px] rounded-sm text-amber-700 border-amber-200 bg-amber-50">not verifiable items</Badge>}
              </div>
              <div className="text-[10px] text-slate-400 font-mono truncate">{c.slug}</div>
              {c.audit && <div className="text-[10px] text-slate-400">Last audited {String(c.audit.audited_at).slice(0, 19).replace('T', ' ')} UTC by {c.audit.audited_by}</div>}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {c.audit ? (
                <div className="flex gap-1.5">
                  {['critical', 'high', 'medium', 'low'].map(s => (
                    <span key={s} className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${SEV[s]}`} title={s}>{c.audit.counts?.[s] || 0}</span>
                  ))}
                </div>
              ) : <span className="text-[10px] text-slate-400">not audited</span>}
              <Button size="sm" variant="outline" className="rounded-sm" disabled={!!busy || auto}
                onClick={() => auditOne(c.slug)} data-testid={`qa-audit-${c.slug}`}>
                {busy === c.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : c.audit ? 'Re-audit' : 'Audit'}
              </Button>
              <Button size="sm" variant="ghost" className="rounded-sm"
                onClick={() => navigate(c.audit ? `/course-qa?audit=${c.audit.id}` : `/course-qa?slug=${c.slug}`)}
                data-testid={`qa-open-${c.slug}`}>
                Open <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}
